import { API_URL } from './env';

/**
 * Report a browser error to the API, which forwards it to Slack. Fire-and-forget
 * and swallows everything: reporting a bug must never itself throw, or a render
 * loop turns into an error loop. Sent with credentials so the gate cookie rides
 * along and the request is not turned away at the door.
 */
let appName = 'web';

/** Names which app a report came from, so Slack says web vs support panel. */
export function setErrorReportApp(name: string): void {
  appName = name;
}

// Client-side dedupe: the same line, over and over, is one bug. The server
// dedupes too, but stopping it here saves a request per repeat.
const recent = new Map<string, number>();
const DEDUPE_MS = 30_000;

/** A stale-deploy artifact, not a bug: the page was built against chunk hashes a
 *  newer deploy has replaced. One reload fetches the fresh set and fixes it. */
function isChunkLoadError(message: string): boolean {
  return /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to load chunk/i.test(message);
}

/** Reload once (guarded against a loop) to pick up the new chunks. Returns true
 *  when it handled the error and reporting should be skipped. */
function handleStaleChunk(): boolean {
  if (typeof window === 'undefined') return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get('_cr')) return false; // already reloaded once, a real problem: let it report
  url.searchParams.set('_cr', '1');
  window.location.replace(url.toString());
  return true;
}

export function reportClientError(input: {
  message: string;
  stack?: string;
  source?: string;
  url?: string;
}): void {
  try {
    // A chunk that failed to load after a deploy is fixed by one reload, not by
    // a bug report: reload and don't log it. If it survives the reload it is a
    // real problem and falls through to reporting.
    if (isChunkLoadError(input.message) && handleStaleChunk()) return;
    const now = Date.now();
    const key = `${input.source ?? ''}|${input.message}`;
    for (const [k, at] of recent) if (now - at > DEDUPE_MS) recent.delete(k);
    if (recent.has(key)) return;
    recent.set(key, now);

    const body = JSON.stringify({
      message: input.message.slice(0, 2000),
      stack: input.stack?.slice(0, 8000),
      source: input.source,
      url: input.url ?? (typeof location !== 'undefined' ? location.href : undefined),
      app: appName,
    });

    // keepalive lets the report survive a navigation that a crash may trigger.
    void fetch(`${API_URL}/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body,
    }).catch(() => {});
  } catch {
    // never let reporting throw
  }
}

let installed = false;

/** Wire the two global browser error channels to the reporter. Idempotent. */
export function installGlobalErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    reportClientError({
      message: e.message || 'Uncaught error',
      stack: e.error instanceof Error ? e.error.stack : undefined,
      source: 'error',
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    reportClientError({
      message:
        reason instanceof Error ? reason.message : `Unhandled rejection: ${String(reason)}`,
      stack: reason instanceof Error ? reason.stack : undefined,
      source: 'unhandledrejection',
    });
  });
}
